/**
 * 基础资源导航：普通搜索、分类、网站直达、文章和免费收录。
 * @copyright 版权所有 (c) 2026 UIED技术团队
 * @license MIT
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  FormEvent,
} from "react";
import {
  BrowserRouter,
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Meta from "./Meta";
import Storefront from "./Storefront";
import RouteScroll from "./RouteScroll";
import {
  FiCompass,
  FiZap,
  FiFileText,
  FiBarChart2,
  FiGift,
  FiCpu,
  FiTrendingUp,
  FiCamera,
  FiVideo,
  FiUserCheck,
  FiShoppingBag,
  FiSliders,
  FiGrid,
  FiArrowUpRight,
} from "react-icons/fi";
import {
  HiOutlineFire,
  HiOutlineSparkles,
  HiOutlineNewspaper,
} from "react-icons/hi2";
import type { IconType } from "react-icons";

export type Item = {
  id: number;
  name: string;
  slug?: string;
  description?: string;
  url?: string;
  icon_url?: string | null;
  category_id?: number;
  category_name?: string;
  seo_title?: string;
  seo_description?: string;
  title?: string;
  excerpt?: string;
  content?: string;
  author?: string;
  create_time?: number;
  hero_title?: string;
  hero_subtitle?: string;
  search_placeholder?: string;
  parent_id?: number;
  icon?: string;
  children?: Item[];
  websites?: Listing;
};
type Listing = { items: Item[]; total: number; page: number; pageSize: number };
type Settings = {
  site_name: string;
  site_title: string;
  description: string;
  keywords: string;
  logo: string;
  favicon: string;
  icp: string;
  homePageSlug: string;
  primaryColor: string;
};
type NavItem = {
  name: string;
  href: string;
  icon: string;
  type: "internal" | "external";
  highlight?: boolean;
  badge?: string;
};
type Channel = {
  slug: string;
  name: string;
  description: string;
  empty: string;
};
type Site = {
  settings: Settings;
  pages: Item[];
  categories: Item[];
  navbar: NavItem[];
  channels: Channel[];
};
const navigationIcons: Record<string, IconType> = {
  ArrowUpRight: FiArrowUpRight,
  Compass: FiCompass,
  Zap: FiZap,
  Newspaper: HiOutlineNewspaper,
  BarChart2: FiBarChart2,
  Gift: FiGift,
  Sparkles: HiOutlineSparkles,
  Cpu: FiCpu,
  Flame: HiOutlineFire,
  Camera: FiCamera,
  Video: FiVideo,
  UserCheck: FiUserCheck,
  FileText: FiFileText,
  TrendingUp: FiTrendingUp,
  ShoppingBag: FiShoppingBag,
  Sliders: FiSliders,
};
function NavigationIcon({ name }: { name?: string }) {
  const Component = (navigationIcons[name || ""] || FiGrid) as (
    props: React.SVGProps<SVGSVGElement>
  ) => React.ReactElement;
  return <Component aria-hidden="true" />;
}
function CategoryLinks({
  items,
  selected,
  parent,
}: {
  items: Item[];
  selected?: number;
  parent?: Item;
}) {
  if (!items.length) return null;
  return (
    <nav className="subcategory-links" aria-label="细分场景">
      {parent && <Link to={"/category/" + parent.slug}
        className={selected === parent.id ? "active" : ""}
        aria-current={selected === parent.id ? "page" : undefined}>全部</Link>}
      {items.map((c) => (
        <Link
          key={c.id}
          to={"/category/" + c.slug}
          className={selected === c.id ? "active" : ""}
          aria-current={selected === c.id ? "page" : undefined}
        >
          {c.name}
        </Link>
      ))}
    </nav>
  );
}
const SiteContext = createContext<Site>(null!);
/** 请求只访问本站的基础接口，展示服务端错误。 */
async function api(path: string, signal?: AbortSignal, body?: unknown) {
  const response = await fetch("/api/basic/" + path, {
    signal,
    method: body ? "POST" : "GET",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json();
  if (!response.ok || payload.code !== 200)
    throw new Error(payload.message || "加载失败");
  return payload.data;
}
/** 路径变化时取消旧请求，避免快速切换时覆盖新页面。 */
function useData<T>(path: string | null) {
  const [state, setState] = useState<{
    data?: T;
    error?: string;
    path: string | null;
  }>({ path });
  useEffect(() => {
    if (!path) return;
    const abort = new AbortController();
    setState({ path });
    api(path, abort.signal)
      .then((data) => setState({ path, data }))
      .catch((error) => {
        if (error.name !== "AbortError")
          setState({ path, error: error.message });
      });
    return () => abort.abort();
  }, [path]);
  return state.path === path ? state : { path };
}
function Message({ error }: { error?: string }) {
  return (
    <div className="message" data-page-ready={error ? "true" : undefined} role={error ? "alert" : "status"}>
      {error || "正在加载…"}
      {error && (
        <p>
          <Link to="/">返回首页</Link>
        </p>
      )}
    </div>
  );
}
function SearchBox({
  placeholder = "搜索电商工具：商品主图、模特换装、选品数据…",
  initial = "",
}: {
  placeholder?: string;
  initial?: string;
}) {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  function search(e: FormEvent) {
    e.preventDefault();
    if (!value.trim()) {
      input.current?.focus();
      return;
    }
    navigate("/search?q=" + encodeURIComponent(value.trim()));
  }
  function clear() {
    setValue("");
    input.current?.focus();
  }
  return (
    <form className="searchbox" role="search" onSubmit={search}>
      <span aria-hidden="true">⌕</span>
      <input
        ref={input}
        aria-label="搜索网站"
        enterKeyHint="search"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") clear(); }}
        maxLength={100}
      />
      {value && <button className="search-clear" type="button" aria-label="清空搜索" onClick={clear}>×</button>}
      <button type="submit" disabled={!value.trim()}>搜索</button>
    </form>
  );
}
function safeUrl(value?: string) {
  return value && /^(https?:\/\/|\/(?!\/))/.test(value) ? value : undefined;
}
function websiteHostname(value?: string) {
  try {
    const url = new URL(value || "");
    return ["http:", "https:"].includes(url.protocol) ? url.hostname : "";
  } catch {
    return "";
  }
}
function WebsiteLogo({ item }: { item: Item }) {
  const hostname = websiteHostname(item.url);
  const [failed, setFailed] = useState<string[]>([]);
  const [loaded, setLoaded] = useState("");
  // Use the same favicon service as the admin's automatic icon action.
  const sources = [
    safeUrl(item.icon_url?.trim()),
    hostname ? `https://favicon.im/${encodeURIComponent(hostname)}?larger=true` : undefined,
  ];
  const source = sources.find((url): url is string => Boolean(url && !failed.includes(url)));
  const initial = Array.from(item.name.trim())[0]?.toUpperCase() || "?";
  const tone = Array.from(hostname || item.name).reduce((sum, char) => sum + char.charCodeAt(0), 0) % 3;
  return <span className={`website-logo website-logo-tone-${tone}`} aria-hidden="true">
    <span>{initial}</span>
    {source && <img src={source} alt="" width={44} height={44}
      loading="lazy" decoding="async" referrerPolicy="no-referrer"
      className={loaded === source ? "is-loaded" : ""}
      onLoad={() => setLoaded(source)}
      onError={() => setFailed(previous => [...previous, source])} />}
  </span>;
}
function Cards({ items }: { items: Item[] }) {
  return (
    <div className="cards">
      {items.map((item) => (
        <a
          className="card website-card"
          key={item.id}
          href={safeUrl(item.url)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="website-card-heading">
            <WebsiteLogo key={`${item.url}|${item.icon_url}`} item={item} />
            <div className="website-card-identity">
              <h3>{item.name}</h3>
              {websiteHostname(item.url) && <span className="website-card-domain">
                {websiteHostname(item.url).replace(/^www\./, "")}
              </span>}
            </div>
            <NavigationIcon name="ArrowUpRight" />
          </div>
          <p>{item.description || "探索这个网站"}</p>
        </a>
      ))}
    </div>
  );
}
function Pagination({ data }: { data: Listing }) {
  const [params] = useSearchParams();
  const location = useLocation();
  if (data.total <= data.pageSize) return null;
  const pageLink = (page: number) => {
    const query = new URLSearchParams(params);
    if (page === 1) query.delete("page");
    else query.set("page", String(page));
    return location.pathname + (query.toString() ? "?" + query.toString() : "");
  };
  return (
    <nav className="pagination" aria-label="分页">
      {data.page > 1 ? (
        <Link to={pageLink(data.page - 1)}>上一页</Link>
      ) : (
        <span aria-disabled="true">上一页</span>
      )}
      <span>
        第 {data.page} / {Math.ceil(data.total / data.pageSize)} 页
      </span>
      {data.page * data.pageSize < data.total ? (
        <Link to={pageLink(data.page + 1)}>下一页</Link>
      ) : (
        <span aria-disabled="true">下一页</span>
      )}
    </nav>
  );
}
function Directory() {
  const { settings } = useContext(SiteContext);
  const { slug } = useParams();
  const { data, error } = useData<{ page: Item; categories: Item[] }>(
    "pages/" + encodeURIComponent(slug || settings.homePageSlug)
  );
  if (!data) return <Message error={error} />;
  const categoryContent = (c: Item) => <>
    <CategoryLinks items={c.children || []} />
    {c.websites?.items.length ? <Cards items={c.websites.items} /> : (
      <div className="empty">
        暂无收录工具。<Link to={"/submit?category=" + c.id}>推荐一个工具 →</Link>
      </div>
    )}
  </>;
  return (
    <Storefront page={data.page} categories={data.categories}
      search={<SearchBox placeholder={data.page.search_placeholder} />}>
      {data.categories.length === 0 && (
        <div className="empty">这个页面还没有分类。</div>
      )}
      {data.categories.map((c) => (
        <section id={"category-" + c.id} key={c.id} className="category-section">
          <div className="section-heading">
            <div>
              <h2><NavigationIcon name={c.icon} />{c.name}
                <span className="section-count">{c.websites?.total || 0}</span>
              </h2>
              <p>{c.description}</p>
            </div>
            <Link to={"/category/" + c.slug}>查看全部 →</Link>
          </div>
          {categoryContent(c)}
        </section>
      ))}
    </Storefront>
  );
}

function WebsiteList({
  mode,
}: {
  mode: "search" | "category" | "tag" | "new";
}) {
  const [params] = useSearchParams();
  const { id, tag } = useParams();
  const site = useContext(SiteContext);
  const category = site.categories.find(
    (c) => c.slug === id || String(c.id) === id
  );
  const parentCategory = category?.parent_id
    ? site.categories.find((c) => c.id === category.parent_id) : category;
  const q = params.get("q") || "";
  const query = new URLSearchParams({
    page: params.get("page") || "1",
    pageSize: "24",
  });
  if (mode === "search") query.set("q", q);
  if (mode === "category") query.set("category", id || "");
  if (mode === "tag") query.set("tag", tag || "");
  if (mode === "new") query.set("order", "new");
  const needsQuery = mode === "search" && !q.trim();
  const { data, error } = useData<Listing>(needsQuery ? null : "websites?" + query);
  const title =
    mode === "category"
      ? category?.name || "分类"
      : mode === "tag"
      ? `标签：${tag}`
      : mode === "new"
      ? "每日上新"
      : "全站搜索";
  return (
    <section className="content" data-page-ready={!!data || !!error || needsQuery ? "true" : undefined}>
      <Link className="breadcrumb" to="/#tool-directory">← 工具大全</Link>
      {category?.parent_id && parentCategory && (
        <Link
          className="breadcrumb"
          to={"/category/" + parentCategory.slug}
        >
          ← {parentCategory.name}
        </Link>
      )}
      <h1>{title}</h1>
      {mode !== "search" && (
        <p className="muted">
          {category?.description || "发现值得收藏的网站与工具。"}
        </p>
      )}
      <SearchBox initial={mode === "search" ? q : ""} />
      {category && (
        <CategoryLinks
          items={site.categories.filter(
            (c) => c.parent_id === (category.parent_id || category.id)
          )}
          selected={category.id}
          parent={parentCategory}
        />
      )}
      {needsQuery ? (
        <div className="empty"><p>输入工具名称或用途，快速找到需要的网站。</p>
          <Link to="/#tool-directory">按场景浏览工具 →</Link></div>
      ) : !data ? (
        <Message error={error} />
      ) : (
        <>
          <p className="result-count" role="status">{mode === "search" ? `“${q}” · ` : ""}共 {data.total} 个网站</p>
          {data.items.length ? (
            <Cards items={data.items} />
          ) : (
            <div className="empty">
              <p>{mode === "search" ? `没有找到与“${q}”相关的工具，试试更短的关键词。` : "这里暂时没有收录工具。"}</p>
              <Link to="/#tool-directory">浏览其他工具 →</Link>
              {category && <Link to={"/submit?category=" + category.id}>推荐工具 →</Link>}
            </div>
          )}
          <Pagination data={data} />
        </>
      )}
    </section>
  );
}
function Articles({ channelSlug }: { channelSlug?: string }) {
  const { channels } = useContext(SiteContext);
  const channel = channels.find((c) => c.slug === channelSlug);
  const [params] = useSearchParams();
  const { data, error } = useData<Listing>(
    "articles?" +
      new URLSearchParams({
        page: params.get("page") || "1",
        ...(channel ? { category: channel.slug } : {}),
      })
  );
  return (
    <section className="content narrow" data-page-ready={data || error ? "true" : undefined}>
      <div className="eyebrow">
        AE86 AI · {channelSlug === "prompts" ? "PROMPTS" : "LEARN & EXPLORE"}
      </div>
      <h1>{channel?.name || "全部文章"}</h1>
      <p className="muted">
        {channel?.description ||
          "围绕商品做图、营销文案、选品与店铺运营，整理 AI 电商实战教程、行业快讯、工具测评和提示词。"}
      </p>
      <nav className="subcategory-links" aria-label="内容栏目">
        {channels.map((c) => (
          <Link
            key={c.slug}
            to={"/" + c.slug}
            aria-current={channelSlug === c.slug ? "page" : undefined}
            className={channelSlug === c.slug ? "active" : ""}
          >
            {c.name}
          </Link>
        ))}
      </nav>
      {!data ? (
        <Message error={error} />
      ) : (
        <>
          {data.items.length ? (
            data.items.map((a) => (
              <Link
                className="article-row"
                to={"/article/" + (a.slug || a.id)}
                key={a.id}
              >
                <h2>{a.title}</h2>
                <p>{a.excerpt}</p>
                <span>阅读文章 →</span>
              </Link>
            ))
          ) : (
            <div className="empty channel-empty">
              <NavigationIcon name="FileText" />
              <p>{channel?.empty || "文章正在整理中，敬请期待。"}</p>
              <Link className="button" to="/#tool-directory">
                浏览工具大全 →
              </Link>
            </div>
          )}
          <Pagination data={data} />
        </>
      )}
    </section>
  );
}
function Article() {
  const { id } = useParams();
  const { data, error } = useData<Item>(
    "articles/" + encodeURIComponent(id || "")
  );
  if (!data) return <Message error={error} />;
  return (
    <article className="content narrow" data-page-ready="true">
      <Link to="/articles">← 全部文章</Link>
      <h1>{data.title}</h1>
      <p className="muted">
        {data.author}{" "}
        {data.create_time
          ? new Date(data.create_time * 1000).toLocaleDateString("zh-CN")
          : ""}
      </p>
      <div
        className="prose"
        dangerouslySetInnerHTML={{ __html: data.content || "" }}
      />
    </article>
  );
}
function Submit() {
  const { categories } = useContext(SiteContext);
  const [params] = useSearchParams();
  const suggestedCategory = categories.find(c => String(c.id) === params.get("category"));
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting.current) return;
    const form = e.currentTarget;
    const values = Object.fromEntries(new FormData(form).entries());
    submitting.current = true;
    setBusy(true);
    setMessage("");
    try {
      await api("submissions", undefined, values);
      setSuccess(true);
      setMessage("提交成功，审核通过后会展示在导航中。");
      form.reset();
    } catch (err) {
      setSuccess(false);
      setMessage((err as Error).message);
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }
  return (
    <section className="content narrow" data-page-ready="true">
      <Link className="breadcrumb" to="/#tool-directory">← 工具大全</Link>
      <h1>分享一个好网站</h1>
      <p className="muted">
        免费提交 AI 电商实用工具，审核通过后收录。
      </p>
      {success ? <div className="panel submission-success" role="status">
        <h2>提交成功</h2><p>{message}</p>
        <Link className="button" to="/#tool-directory">继续浏览工具</Link>
        <button className="secondary-button" type="button" onClick={() => { setSuccess(false); setMessage(""); }}>再推荐一个</button>
      </div> : <form className="panel form" onSubmit={submit} aria-busy={busy}>
        <label>
          网站名称
          <input name="name" required maxLength={200} />
        </label>
        <label>
          网站地址
          <input
            name="url"
            type="url"
            placeholder="https://"
            required
            maxLength={500}
          />
        </label>
        <label>
          资源分类
          <select name="category_id" required defaultValue={suggestedCategory?.id || ""}>
            <option value="" disabled>
              请选择分类
            </option>
            {categories.filter(c => !c.parent_id).map(root => <optgroup label={root.name} key={root.id}>
              <option value={root.id}>{root.name}（通用）</option>
              {categories.filter(c => c.parent_id === root.id).map(c => <option value={c.id} key={c.id}>{c.name}</option>)}
            </optgroup>)}
          </select>
        </label>
        <label>
          网站简介（选填）
          <textarea name="description" rows={3} maxLength={2000} placeholder="一句话介绍它能帮你做什么" />
        </label>
        <label>
          联系邮箱（选填）
          <input name="email" type="email" maxLength={200} />
        </label>
        {message && (
          <p role="alert" className="error">
            {message}
          </p>
        )}
        <button disabled={busy} type="submit">
          {busy ? "提交中…" : "提交收录"}
        </button>
      </form>}
    </section>
  );
}
function Missing() {
  return (
    <section className="content narrow" data-page-ready="true">
      <h1>页面不存在</h1>
      <p>这个页面已移除或地址有误。</p>
      <Link className="button" to="/">
        返回首页
      </Link>
    </section>
  );
}
function SiteLayout() {
  const site = useContext(SiteContext);
  const { pathname, search } = useLocation();
  useEffect(() => {
    const nav = document.querySelector<HTMLElement>("header.navbar nav");
    const active = nav?.querySelector<HTMLElement>("[aria-current='page']");
    if (!nav || !active) return;
    const item = active.getBoundingClientRect();
    const bounds = nav.getBoundingClientRect();
    if (item.left < bounds.left || item.right > bounds.right) {
      nav.scrollLeft += item.left - bounds.left - (bounds.width - item.width) / 2;
    }
  }, [pathname]);
  return (
    <>
      <Meta />
      <RouteScroll />
      <Link className="skip-link" to={pathname + search + "#main-content"}>跳到主要内容</Link>
      <header className="navbar storefront-navbar">
        <Link className="brand" to="/">
          {safeUrl(site.settings.logo) && (
            <img
              src={safeUrl(site.settings.logo)}
              width={40}
              height={40}
              alt=""
            />
          )}
          <strong>{site.settings.site_name}</strong>
        </Link>
        <nav aria-label="主导航">
          {site.navbar.map((item) => {
            const content = (
              <>
                <NavigationIcon name={item.icon} />
                <span>{item.name}</span>
                {item.badge && <span className="nav-badge">{item.badge}</span>}
              </>
            );
            const active =
              item.href === "/"
                ? pathname === "/" ||
                  /^\/(category|p|new|search|tag)(\/|$)/.test(pathname)
                : pathname === item.href;
            return item.type === "external" ? (
              <a
                key={item.href}
                href={safeUrl(item.href)}
                className={item.highlight ? "nav-highlight" : ""}
                target="_blank"
                rel="noopener noreferrer"
              >
                {content}
              </a>
            ) : (
              <Link
                key={item.href}
                to={item.href}
                className={active ? "active" : ""}
                aria-current={active ? "page" : undefined}
              >
                {content}
              </Link>
            );
          })}
        </nav>
        <Link className="nav-submit" to="/submit">
          提交网站 ↗
        </Link>
      </header>
      <main id="main-content">
        <Routes>
          <Route path="/" element={<Directory />} />
          <Route path="/p/:slug" element={<Directory />} />
          <Route path="/search" element={<WebsiteList mode="search" />} />
          <Route
            path="/category/:id"
            element={<WebsiteList mode="category" />}
          />
          <Route path="/tag/:tag" element={<WebsiteList mode="tag" />} />
          <Route path="/new" element={<WebsiteList mode="new" />} />
          <Route path="/articles" element={<Articles />} />
          {site.channels.map((channel) => (
            <Route
              key={channel.slug}
              path={"/" + channel.slug}
              element={<Articles channelSlug={channel.slug} />}
            />
          ))}
          <Route path="/article/:id" element={<Article />} />
          <Route path="/articles/:id" element={<Article />} />
          <Route path="/submit" element={<Submit />} />
          <Route path="*" element={<Missing />} />
        </Routes>
      </main>
      <footer>
        <div>
          <strong>{site.settings.site_name}</strong>
          <p>{site.settings.description}</p>
          <nav className="footer-links" aria-label="内容栏目与收录">
            {site.channels.map((channel) => (
              <Link key={channel.slug} to={"/" + channel.slug}>
                {channel.name}
              </Link>
            ))}
            <Link to="/submit">推荐工具</Link>
          </nav>
        </div>
        <div>
          © {new Date().getFullYear()} {site.settings.site_name} · 专注 AI
          电商工具导航
          {site.settings.icp && (
            <p>
              <a
                href="https://beian.miit.gov.cn/"
                target="_blank"
                rel="noreferrer"
              >
                {site.settings.icp}
              </a>
            </p>
          )}
        </div>
      </footer>
    </>
  );
}
export default function App() {
  const { data, error } = useData<Site>("site");
  useEffect(() => {
    if (data) {
      document.documentElement.style.setProperty(
        "--primary",
        data.settings.primaryColor
      );
      const icon = document.querySelector('link[rel="icon"]');
      if (icon && safeUrl(data.settings.favicon))
        icon.setAttribute("href", data.settings.favicon);
    }
  }, [data]);
  return (
    <BrowserRouter>
      {data ? (
        <SiteContext.Provider value={data}>
          <SiteLayout />
        </SiteContext.Provider>
      ) : (
        <Message error={error} />
      )}
    </BrowserRouter>
  );
}
